import { inject, Injectable } from '@angular/core';

import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';

import { AnalyticsProperties, AnalyticsService } from './analytics.service';

interface AppcuesApi {
  loadLaunchpad(
    selector: string,
    options: {
      position: string;
      header: string;
      icon: string;
    },
  ): void;
}

interface AnalyticsEventsWindow extends Window {
  Appcues?: AppcuesApi;
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsEventsService {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly userStateService = inject(UserStateService);
  private readonly companyStateService = inject(CompanyStateService);
  private readonly broadcasterService = inject(BroadcasterService);
  private initialized = false;

  identify(): void {
    const username = this.userStateService.getUsername();

    if (username) {
      const profile = this.userStateService.getCopyOfProfile();
      const properties: AnalyticsProperties = {
        userId: username,
        email: profile.email,
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        companyRole: profile.companyRole || '',
        roles: profile.roles?.length > 0 ? profile.roles : '',
        registeredDate: profile.termsAcceptanceDate,
        loginMethod: this.userStateService.getLoginMethod(),
      };

      if (this.companyStateService.getUserCompanyId()) {
        const company = this.companyStateService.getCopyOfUserCompany();

        properties.companyId = company.id;
        properties.companyName = company.name;
        properties.companyIndustry = company.companyIndustry;
        properties.parentId = company.parentId;
        properties.company = {
          id: company.id,
          name: company.name,
          companyIndustry: company.companyIndustry,
          parentId: company.parentId,
        };
        properties.subscriptionStatus =
          company.planSubscriptionStatus || 'Free';
        properties.subscriptionRenewalDate = company.planCurrentPeriodEndDate;
        properties.subscriptionTrialExpiryDate = company.planTrialExpiryDate;
      }

      this.analyticsService.identify(username, properties);

      if (profile.termsAcceptanceDate) {
        this.analyticsService.track('logged in', {
          ...properties,
          loginDate: new Date(),
        });
      }
    }

    this.loadAppcuesLaunchpad();
  }

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.broadcasterService.on('risevision.user.authorized', () => {
      this.identify();
    });
  }

  private loadAppcuesLaunchpad(): void {
    this.analyticsWindow.Appcues?.loadLaunchpad('#appcues-launchpad', {
      position: 'left',
      header: '<div><strong>Announcements</strong></div>',
      icon: 'https://storage.googleapis.com/widgets.risevision.com/stable/templates/template-dependences/gsap/launch-pad-icon.svg',
    });
  }

  private get analyticsWindow(): AnalyticsEventsWindow {
    return window as unknown as AnalyticsEventsWindow;
  }
}
